import { getSupabase } from './supabase';
import { CustomerOrder, OrderItem } from '../types';
import { getApiUrl } from './api';
import { formatRupiah } from './formatters';

export type MembershipTierLevel = 'SILVER' | 'GOLD' | 'PLATINUM';

export interface MembershipTierSettings {
  id: string;
  silverMinTransactions: number;
  goldMinTransactions: number;
  platinumMinTransactions: number;
  silverMinSpending: number;
  goldMinSpending: number;
  platinumMinSpending: number;
  silverMinReferrals?: number;
  goldMinReferrals?: number;
  platinumMinReferrals?: number;
  updatedAt?: string;
  updatedBy?: string;
}

export interface CalculatedCustomerTier {
  tier: MembershipTierLevel;
  tierName: string;
  tierBadge: string;
  transactionCount: number;
  validSpending: number;
  successfulReferrals: number;
  nextTier: MembershipTierLevel | null;
  nextTierName: string | null;
  remainingTransactions: number;
  remainingSpending: number;
  remainingReferrals: number;
  isTransactionsMet: boolean;
  isSpendingMet: boolean;
  progressPercent: number;
  statusMessage: string;
  theme: {
    badgeBg: string;
    badgeText: string;
    cardGradient: string;
    accentColor: string;
    borderAccent: string;
    iconEmoji: string;
  };
}

export const DEFAULT_MEMBERSHIP_TIER_SETTINGS: MembershipTierSettings = {
  id: 'default',
  silverMinTransactions: 0,
  goldMinTransactions: 100,
  platinumMinTransactions: 250,
  silverMinSpending: 0,
  goldMinSpending: 0,
  platinumMinSpending: 0,
  silverMinReferrals: 0,
  goldMinReferrals: 25,
  platinumMinReferrals: 50,
};

const TIER_STORAGE_CACHE_KEY = 'leton_membership_tier_settings_cache';

/**
 * Filter function to determine if an order from public.orders is valid for transaction count.
 * 
 * STRICT RULES (QRIS PAYMENT FLOW):
 * 1. Order Status: CANCELLED, REJECTED, and FAILED orders are strictly EXCLUDED.
 * 2. Payment Status:
 *    - EXCLUDED: WAITING PAYMENT, WAITING VERIFICATION, PAYMENT REJECTED, REJECTED.
 *    - INCLUDED: PAID (verified QRIS payment) or COMPLETED non-rejected orders.
 * 3. Order Items Integrity:
 *    - Must have verified order items in database with quantity > 0.
 *    - If an order was inserted into orders table but order_items insertion failed/empty, it is STRICTLY EXCLUDED.
 */
export function isOrderValidForTier(order: {
  orderStatus?: string;
  order_status?: string;
  paymentStatus?: string;
  payment_status?: string;
  items?: any[];
  order_items?: any[];
}): boolean {
  const os = (order.orderStatus || order.order_status || '').toUpperCase().trim();
  const ps = (order.paymentStatus || order.payment_status || '').toUpperCase().trim();

  // 1. Order Status Integrity: Exclude cancelled / rejected / failed
  if (os === 'CANCELLED' || os === 'REJECTED' || os === 'FAILED') {
    return false;
  }

  // 2. Payment Status Integrity: Exclude non-successful payments
  // Customers checkout without paying or waiting for verification do NOT count
  if (ps === 'WAITING PAYMENT' || ps === 'WAITING_PAYMENT') {
    return false;
  }
  if (ps === 'WAITING VERIFICATION' || ps === 'WAITING_VERIFICATION') {
    return false;
  }
  if (ps === 'PAYMENT REJECTED' || ps === 'REJECTED' || ps === 'FAILED' || ps === 'CANCELLED' || ps === 'UNPAID') {
    return false;
  }

  // Payment must be PAID, or Pay-at-Store with order_status COMPLETED
  if (ps !== 'PAID' && os !== 'COMPLETED') {
    return false;
  }

  // 3. Order Items Integrity: Must have valid item details in order_items or items
  if ('order_items' in order && order.order_items !== undefined) {
    if (!Array.isArray(order.order_items) || order.order_items.length === 0) {
      // Order had no order_items recorded in the database
      return false;
    }
    const hasValidItem = order.order_items.some((it: any) => Number(it?.quantity ?? 1) > 0);
    if (!hasValidItem) return false;
  } else if ('items' in order && order.items !== undefined) {
    if (!Array.isArray(order.items) || order.items.length === 0) {
      return false;
    }
    const hasValidItem = order.items.some((it: any) => Number(it?.quantity ?? 1) > 0);
    if (!hasValidItem) return false;
  } else {
    return false;
  }

  return true;
}

/**
 * Normalizes raw membership tier settings payload from any source (Supabase, API, JSON string, snake_case, camelCase).
 */
export function normalizeMembershipTierSettings(raw: any): MembershipTierSettings {
  let c = raw;
  if (typeof c === 'string') {
    try {
      c = JSON.parse(c);
    } catch {
      c = {};
    }
  }
  if (!c || typeof c !== 'object') {
    c = {};
  }

  const parseNum = (val: any, fallback: number): number => {
    if (val === undefined || val === null || val === '') return fallback;
    if (typeof val === 'number') {
      return Number.isFinite(val) ? Math.round(val) : fallback;
    }
    if (typeof val === 'string') {
      const trimmed = val.trim();
      if (!trimmed) return fallback;
      const direct = Number(trimmed);
      if (Number.isFinite(direct)) return Math.round(direct);
      const isNegative = /^-\s*|\(\s*.*\s*\)/.test(trimmed) || trimmed.includes('-');
      const clean = trimmed.replace(/[^0-9]/g, '');
      if (clean === '') return fallback;
      const num = parseInt(clean, 10);
      if (isNaN(num)) return fallback;
      return isNegative ? -num : num;
    }
    const num = Number(val);
    return isNaN(num) ? fallback : Math.round(num);
  };

  return {
    id: 'default',
    silverMinTransactions: parseNum(
      c.silverMinTransactions ?? c.silver_min_transactions,
      DEFAULT_MEMBERSHIP_TIER_SETTINGS.silverMinTransactions
    ),
    goldMinTransactions: parseNum(
      c.goldMinTransactions ?? c.gold_min_transactions,
      DEFAULT_MEMBERSHIP_TIER_SETTINGS.goldMinTransactions
    ),
    platinumMinTransactions: parseNum(
      c.platinumMinTransactions ?? c.platinum_min_transactions,
      DEFAULT_MEMBERSHIP_TIER_SETTINGS.platinumMinTransactions
    ),
    silverMinSpending: Math.max(0, parseNum(
      c.silverMinSpending ?? c.silver_min_spending,
      DEFAULT_MEMBERSHIP_TIER_SETTINGS.silverMinSpending
    )),
    goldMinSpending: Math.max(0, parseNum(
      c.goldMinSpending ?? c.gold_min_spending,
      DEFAULT_MEMBERSHIP_TIER_SETTINGS.goldMinSpending
    )),
    platinumMinSpending: Math.max(0, parseNum(
      c.platinumMinSpending ?? c.platinum_min_spending,
      DEFAULT_MEMBERSHIP_TIER_SETTINGS.platinumMinSpending
    )),
    silverMinReferrals: parseNum(
      c.silverMinReferrals ?? c.silver_min_referrals,
      DEFAULT_MEMBERSHIP_TIER_SETTINGS.silverMinReferrals ?? 0
    ),
    goldMinReferrals: parseNum(
      c.goldMinReferrals ?? c.gold_min_referrals,
      DEFAULT_MEMBERSHIP_TIER_SETTINGS.goldMinReferrals ?? 25
    ),
    platinumMinReferrals: parseNum(
      c.platinumMinReferrals ?? c.platinum_min_referrals,
      DEFAULT_MEMBERSHIP_TIER_SETTINGS.platinumMinReferrals ?? 50
    ),
    updatedAt: c.updatedAt || c.updated_at || c.updated_At,
    updatedBy: c.updatedBy || c.updated_by || c.updated_By,
  };
}

/**
 * Fetch Membership Tier Settings.
 * Prioritizes trusted backend API /api/membership-tier-settings, with fallback to Supabase leton_content and local cache.
 */
export async function getMembershipTierSettings(): Promise<{ settings: MembershipTierSettings; isFallback: boolean }> {
  // 1. Direct Supabase leton_content read (Authoritative Production Source of Truth)
  try {
    const client = getSupabase();
    if (client && typeof client.from === 'function') {
      const { data: contentRow, error: contentErr } = await client
        .from('leton_content')
        .select('*')
        .eq('id', 'membership_tier_settings')
        .maybeSingle();

      if (!contentErr && contentRow && contentRow.content) {
        const parsed = normalizeMembershipTierSettings(contentRow.content);
        if (contentRow.updated_at) {
          parsed.updatedAt = contentRow.updated_at;
        }

        if (typeof window !== 'undefined') {
          localStorage.setItem(TIER_STORAGE_CACHE_KEY, JSON.stringify(parsed));
        }

        return { settings: parsed, isFallback: false };
      }
    }
  } catch (err) {
    console.warn('[MembershipTier] Supabase read note:', err);
  }

  // 2. Try Trusted Server Backend API
  try {
    const res = await fetch(getApiUrl('/api/membership-tier-settings'), {
      cache: 'no-store',
    });
    if (res.ok) {
      const json = await res.json();
      if (json && json.settings) {
        const parsed = normalizeMembershipTierSettings(json.settings);
        if (typeof window !== 'undefined') {
          localStorage.setItem(TIER_STORAGE_CACHE_KEY, JSON.stringify(parsed));
        }
        return { settings: parsed, isFallback: false };
      }
    }
  } catch (apiErr) {
    // Network fallback
  }

  // 3. Local Cache Fallback
  if (typeof window !== 'undefined') {
    const cached = localStorage.getItem(TIER_STORAGE_CACHE_KEY);
    if (cached) {
      try {
        const parsed = normalizeMembershipTierSettings(JSON.parse(cached));
        return { settings: parsed, isFallback: true };
      } catch {}
    }
  }

  return { settings: DEFAULT_MEMBERSHIP_TIER_SETTINGS, isFallback: true };
}

export interface MembershipTierPersistenceDeps {
  getSupabaseClient?: (role?: string) => any;
  fetchImpl?: typeof fetch;
  getAdminToken?: () => string;
  getStoredRole?: () => string;
  setLocalCache?: (settings: MembershipTierSettings) => void;
}

/**
 * Compares all threshold fields between expected and actual MembershipTierSettings.
 */
export function areMembershipTierThresholdsEqual(
  expected: MembershipTierSettings,
  actual: MembershipTierSettings
): boolean {
  if (!expected || !actual) return false;
  return (
    Math.round(Number(expected.silverMinTransactions)) === Math.round(Number(actual.silverMinTransactions)) &&
    Math.round(Number(expected.goldMinTransactions)) === Math.round(Number(actual.goldMinTransactions)) &&
    Math.round(Number(expected.platinumMinTransactions)) === Math.round(Number(actual.platinumMinTransactions)) &&
    Math.round(Number(expected.silverMinSpending ?? 0)) === Math.round(Number(actual.silverMinSpending ?? 0)) &&
    Math.round(Number(expected.goldMinSpending ?? 0)) === Math.round(Number(actual.goldMinSpending ?? 0)) &&
    Math.round(Number(expected.platinumMinSpending ?? 0)) === Math.round(Number(actual.platinumMinSpending ?? 0)) &&
    Math.round(Number(expected.silverMinReferrals ?? 0)) === Math.round(Number(actual.silverMinReferrals ?? 0)) &&
    Math.round(Number(expected.goldMinReferrals ?? DEFAULT_MEMBERSHIP_TIER_SETTINGS.goldMinReferrals ?? 25)) ===
      Math.round(Number(actual.goldMinReferrals ?? DEFAULT_MEMBERSHIP_TIER_SETTINGS.goldMinReferrals ?? 25)) &&
    Math.round(Number(expected.platinumMinReferrals ?? DEFAULT_MEMBERSHIP_TIER_SETTINGS.platinumMinReferrals ?? 50)) ===
      Math.round(Number(actual.platinumMinReferrals ?? DEFAULT_MEMBERSHIP_TIER_SETTINGS.platinumMinReferrals ?? 50))
  );
}

/**
 * Reads membership tier settings strictly from the authoritative database source (leton_content)
 * without falling back to localStorage or default values.
 */
export async function readAuthoritativeMembershipTierSettings(
  role?: string,
  deps?: MembershipTierPersistenceDeps
): Promise<{ success: boolean; settings?: MembershipTierSettings; error?: string }> {
  const getSupabaseFn = deps?.getSupabaseClient || getSupabase;
  try {
    const client = getSupabaseFn(role);
    if (!client || typeof client.from !== 'function') {
      return {
        success: false,
        error: 'Klien database Supabase belum terinisialisasi untuk verifikasi data.',
      };
    }

    const { data: contentRow, error: contentErr } = await client
      .from('leton_content')
      .select('*')
      .eq('id', 'membership_tier_settings')
      .maybeSingle();

    if (contentErr) {
      return {
        success: false,
        error: `Gagal membaca ulang pengaturan tier dari database: ${contentErr.message || 'Database read error'}`,
      };
    }

    if (!contentRow || contentRow.content === undefined || contentRow.content === null) {
      return {
        success: false,
        error: 'Data pengaturan tier tidak ditemukan di database setelah penyimpanan.',
      };
    }

    const parsed = normalizeMembershipTierSettings(contentRow.content);
    if (contentRow.updated_at) {
      parsed.updatedAt = contentRow.updated_at;
    }

    return { success: true, settings: parsed };
  } catch (err: any) {
    return {
      success: false,
      error: `Kesalahan saat memverifikasi data dari database: ${err?.message || 'Unknown error'}`,
    };
  }
}

/**
 * Save Membership Tier Settings to Trusted Backend API & Supabase leton_content,
 * followed by authoritative read-back verification.
 * ENFORCEMENT: Only Super Admin / Admin Pusat can update threshold.
 */
export async function saveMembershipTierSettings(
  newSettings: MembershipTierSettings,
  adminRole?: string,
  adminUsername?: string,
  deps?: MembershipTierPersistenceDeps
): Promise<{ success: boolean; settings?: MembershipTierSettings; error?: string }> {
  const parseInputNum = (val: any, fallback: number): number => {
    if (val === undefined || val === null || val === '') return fallback;
    if (typeof val === 'number') {
      return Number.isFinite(val) ? Math.round(val) : NaN;
    }
    if (typeof val === 'string') {
      const trimmed = val.trim();
      if (!trimmed) return fallback;
      const direct = Number(trimmed);
      if (Number.isFinite(direct)) return Math.round(direct);
      const isNegative = /^-\s*|\(\s*.*\s*\)/.test(trimmed) || trimmed.includes('-');
      const clean = trimmed.replace(/[^0-9]/g, '');
      if (clean === '') return NaN;
      const num = parseInt(clean, 10);
      if (isNaN(num)) return NaN;
      return isNegative ? -num : num;
    }
    const num = Number(val);
    return isNaN(num) ? NaN : Math.round(num);
  };

  // Validate hierarchy on client before sending (supports formatted strings and rejects negative formatted strings)
  const silver = parseInputNum((newSettings as any).silverMinTransactions, NaN);
  const gold = parseInputNum((newSettings as any).goldMinTransactions, NaN);
  const platinum = parseInputNum((newSettings as any).platinumMinTransactions, NaN);

  const silverSpending = parseInputNum((newSettings as any).silverMinSpending ?? 0, 0);
  const goldSpending = parseInputNum((newSettings as any).goldMinSpending ?? 0, 0);
  const platinumSpending = parseInputNum((newSettings as any).platinumMinSpending ?? 0, 0);

  const silverRef = parseInputNum((newSettings as any).silverMinReferrals ?? 0, 0);
  const goldRef = parseInputNum(
    (newSettings as any).goldMinReferrals ?? DEFAULT_MEMBERSHIP_TIER_SETTINGS.goldMinReferrals,
    DEFAULT_MEMBERSHIP_TIER_SETTINGS.goldMinReferrals ?? 25
  );
  const platinumRef = parseInputNum(
    (newSettings as any).platinumMinReferrals ?? DEFAULT_MEMBERSHIP_TIER_SETTINGS.platinumMinReferrals,
    DEFAULT_MEMBERSHIP_TIER_SETTINGS.platinumMinReferrals ?? 50
  );

  if (isNaN(silver) || silver < 0) {
    return { success: false, error: 'Threshold Transaksi Silver minimal 0.' };
  }
  if (isNaN(gold) || gold < 0 || gold <= silver) {
    return { success: false, error: `Threshold Transaksi Gold (${gold}) harus lebih besar dari Silver (${silver}).` };
  }
  if (isNaN(platinum) || platinum < 0 || platinum <= gold) {
    return { success: false, error: `Threshold Transaksi Platinum (${platinum}) harus lebih besar dari Gold (${gold}).` };
  }

  if (isNaN(silverSpending) || silverSpending < 0) {
    return { success: false, error: 'Ambang Belanja Silver minimal Rp0.' };
  }
  if (isNaN(goldSpending) || goldSpending < 0 || goldSpending < silverSpending) {
    return { success: false, error: `Ambang Belanja Gold (${formatRupiah(goldSpending)}) tidak boleh lebih rendah dari Silver (${formatRupiah(silverSpending)}).` };
  }
  if (isNaN(platinumSpending) || platinumSpending < 0 || platinumSpending < goldSpending) {
    return { success: false, error: `Ambang Belanja Platinum (${formatRupiah(platinumSpending)}) tidak boleh lebih rendah dari Gold (${formatRupiah(goldSpending)}).` };
  }

  if (isNaN(silverRef) || silverRef < 0) {
    return { success: false, error: 'Threshold Referral Silver minimal 0.' };
  }
  if (isNaN(goldRef) || goldRef < 0 || goldRef <= silverRef) {
    return { success: false, error: `Threshold Referral Gold (${goldRef}) harus lebih besar dari Silver (${silverRef}).` };
  }
  if (isNaN(platinumRef) || platinumRef < 0 || platinumRef <= goldRef) {
    return { success: false, error: `Threshold Referral Platinum (${platinumRef}) harus lebih besar dari Gold (${goldRef}).` };
  }

  const adminToken = deps?.getAdminToken
    ? deps.getAdminToken()
    : typeof window !== 'undefined'
      ? localStorage.getItem('leton_admin_token') || ''
      : '';
  const storedRole = deps?.getStoredRole
    ? deps.getStoredRole()
    : typeof window !== 'undefined'
      ? localStorage.getItem('leton_admin_role') || ''
      : '';
  const role = adminRole || storedRole;
  const normalizedRole = (role || '').toLowerCase().trim();

  if (normalizedRole !== 'super_admin' && normalizedRole !== 'root') {
    return {
      success: false,
      error: 'Akses Ditolak: Hanya Super Admin / Admin Pusat yang dapat mengubah threshold tier.',
    };
  }

  const nowIso = new Date().toISOString();
  const payloadToSave: MembershipTierSettings = {
    id: 'default',
    silverMinTransactions: Math.round(silver),
    goldMinTransactions: Math.round(gold),
    platinumMinTransactions: Math.round(platinum),
    silverMinSpending: Math.round(silverSpending),
    goldMinSpending: Math.round(goldSpending),
    platinumMinSpending: Math.round(platinumSpending),
    silverMinReferrals: Math.round(silverRef),
    goldMinReferrals: Math.round(goldRef),
    platinumMinReferrals: Math.round(platinumRef),
    updatedAt: nowIso,
    updatedBy: adminUsername || 'Super Admin',
  };

  const fetchFn = deps?.fetchImpl || fetch;

  // 1. Single Authoritative Write Path: Server API Endpoint (/api/admin/membership-tier-settings)
  // Checks all HTTP status codes (400, 401, 403, 404, 500, 502, 503) without browser dual-write
  try {
    const res = await fetchFn(getApiUrl('/api/admin/membership-tier-settings'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${adminToken}`,
        'x-admin-role': normalizedRole,
      },
      body: JSON.stringify(payloadToSave),
    });

    let json: any = null;
    try {
      json = await res.json();
    } catch {
      json = null;
    }

    if (!res.ok || !json || json.success !== true) {
      if (res.status === 403) {
        return {
          success: false,
          error: json?.error || 'Akses Ditolak: Hanya Super Admin / Admin Pusat yang dapat mengubah threshold tier.',
        };
      }
      if (res.status === 401) {
        return {
          success: false,
          error: json?.error || 'Sesi admin tidak valid atau telah kedaluwarsa. Silakan login kembali sebagai Super Admin.',
        };
      }
      return {
        success: false,
        error: json?.error || `Gagal menyimpan pengaturan tier pada server (HTTP ${res.status}).`,
      };
    }

    if (json.settings) {
      const serverNormalized = normalizeMembershipTierSettings(json.settings);
      if (!areMembershipTierThresholdsEqual(payloadToSave, serverNormalized)) {
        return {
          success: false,
          error: 'Verifikasi penyimpanan gagal: Nilai yang dikembalikan server tidak cocok dengan nilai yang disimpan.',
        };
      }
    }
  } catch (apiErr: any) {
    return {
      success: false,
      error: `Gagal menghubungi server backend: ${apiErr?.message || 'Network error'}`,
    };
  }

  // 3. Authoritative post-save read-back verification from leton_content
  const verification = await readAuthoritativeMembershipTierSettings(normalizedRole, deps);
  if (!verification.success || !verification.settings) {
    return {
      success: false,
      error: verification.error || 'Gagal membaca ulang konfigurasi dari database untuk verifikasi pasca-simpan.',
    };
  }

  const verifiedSettings = normalizeMembershipTierSettings(verification.settings);
  if (!areMembershipTierThresholdsEqual(payloadToSave, verifiedSettings)) {
    return {
      success: false,
      error: 'Verifikasi penyimpanan gagal: Nilai di database tidak cocok dengan nilai yang disimpan.',
    };
  }

  // 4. Only update local cache & notify UI after authoritative verification succeeds
  if (deps?.setLocalCache) {
    deps.setLocalCache(verifiedSettings);
  } else if (typeof window !== 'undefined') {
    localStorage.setItem(TIER_STORAGE_CACHE_KEY, JSON.stringify(verifiedSettings));
    window.dispatchEvent(new CustomEvent('leton_tier_settings_updated', { detail: verifiedSettings }));
  }

  return { success: true, settings: verifiedSettings };
}

/**
 * Coordinates save operations and Supabase Realtime events without fixed timeouts,
 * ensuring external Realtime updates arriving during an active save are queued and
 * reconciled once the save finishes without causing infinite loops or overwriting form state on failure.
 */
export function createMembershipTierSaveCoordinator(options: {
  saveFn?: typeof saveMembershipTierSettings;
  readAuthoritativeFn?: typeof readAuthoritativeMembershipTierSettings;
  onApplySettings: (settings: MembershipTierSettings) => void;
  onStatus: (status: { type: 'success' | 'error'; text: string } | null) => void;
  onSavingChange?: (saving: boolean) => void;
  onSuccessAfterVerify?: (settings: MembershipTierSettings) => void;
  deps?: MembershipTierPersistenceDeps;
}) {
  const saveFn = options.saveFn || saveMembershipTierSettings;
  const readAuthoritativeFn = options.readAuthoritativeFn || readAuthoritativeMembershipTierSettings;
  let isSaving = false;
  let pendingRealtimeReload = false;
  let lastSavedRole: string | undefined;

  const reconcileFromAuthoritative = async (role?: string) => {
    const res = await readAuthoritativeFn(role ?? lastSavedRole, options.deps);
    if (res.success && res.settings) {
      const normalized = normalizeMembershipTierSettings(res.settings);
      options.onApplySettings(normalized);
      return normalized;
    }
    return null;
  };

  return {
    getIsSaving: () => isSaving,
    hasPendingRealtimeReload: () => pendingRealtimeReload,
    handleRealtimeEvent: async (role?: string) => {
      if (isSaving) {
        pendingRealtimeReload = true;
        return { queued: true, reloaded: false };
      }
      const updated = await reconcileFromAuthoritative(role);
      return { queued: false, reloaded: Boolean(updated), settings: updated };
    },
    executeSave: async (
      payloadToSave: MembershipTierSettings,
      adminRole?: string,
      adminUsername?: string
    ): Promise<{ success: boolean; settings?: MembershipTierSettings; error?: string }> => {
      isSaving = true;
      pendingRealtimeReload = false;
      lastSavedRole = adminRole;
      options.onSavingChange?.(true);
      options.onStatus(null);

      try {
        const res = await saveFn(payloadToSave, adminRole, adminUsername, options.deps);
        if (!res.success || !res.settings) {
          const errMsg = res.error || 'Gagal menyimpan pengaturan tier.';
          options.onStatus({ type: 'error', text: errMsg });
          return { success: false, error: errMsg };
        }

        const verified = normalizeMembershipTierSettings(res.settings);
        if (!areMembershipTierThresholdsEqual(payloadToSave, verified)) {
          const mismatchMsg = 'Verifikasi penyimpanan gagal: Nilai di database tidak cocok dengan nilai yang disimpan.';
          options.onStatus({ type: 'error', text: mismatchMsg });
          return { success: false, error: mismatchMsg };
        }

        options.onApplySettings(verified);
        options.onStatus({
          type: 'success',
          text: 'Konfigurasi Membership Tier berhasil disimpan ke database Supabase!',
        });
        options.onSuccessAfterVerify?.(verified);
        return { success: true, settings: verified };
      } catch (err: any) {
        const errMsg = err?.message || 'Terjadi kesalahan sistem.';
        options.onStatus({ type: 'error', text: errMsg });
        return { success: false, error: errMsg };
      } finally {
        isSaving = false;
        options.onSavingChange?.(false);
        if (pendingRealtimeReload) {
          pendingRealtimeReload = false;
          await reconcileFromAuthoritative(adminRole);
        }
      }
    },
  };
}

/**
 * Calculate Customer Membership Tier dynamically based on verified valid transaction count
 * AND accumulated valid spending against thresholds configured by Admin Pusat.
 * STRICT AND RULE: Both transaction count AND spending must meet or exceed the threshold for tier upgrade.
 */
export function calculateMembershipTier(
  transactionCount: number,
  spendingOrSettings?: number | MembershipTierSettings,
  referralsOrSettingsParam?: number | MembershipTierSettings,
  settingsParam?: MembershipTierSettings
): CalculatedCustomerTier {
  const count = Math.max(0, Number(transactionCount) || 0);
  let spending = 0;
  let refCount = 0;
  let settings: MembershipTierSettings = DEFAULT_MEMBERSHIP_TIER_SETTINGS;

  if (typeof spendingOrSettings === 'object' && spendingOrSettings !== null) {
    settings = spendingOrSettings;
  } else {
    spending = Math.max(0, Number(spendingOrSettings) || 0);
    if (typeof referralsOrSettingsParam === 'object' && referralsOrSettingsParam !== null) {
      settings = referralsOrSettingsParam;
    } else {
      refCount = Math.max(0, Number(referralsOrSettingsParam) || 0);
      if (settingsParam && typeof settingsParam === 'object') {
        settings = settingsParam;
      }
    }
  }

  const silverMinTx = Math.max(0, Number(settings.silverMinTransactions ?? DEFAULT_MEMBERSHIP_TIER_SETTINGS.silverMinTransactions));
  const goldMinTx = Math.max(silverMinTx + 1, Number(settings.goldMinTransactions ?? DEFAULT_MEMBERSHIP_TIER_SETTINGS.goldMinTransactions));
  const platinumMinTx = Math.max(goldMinTx + 1, Number(settings.platinumMinTransactions ?? DEFAULT_MEMBERSHIP_TIER_SETTINGS.platinumMinTransactions));

  const silverMinSpending = Math.max(0, Number(settings.silverMinSpending ?? DEFAULT_MEMBERSHIP_TIER_SETTINGS.silverMinSpending));
  const goldMinSpending = Math.max(0, Number(settings.goldMinSpending ?? DEFAULT_MEMBERSHIP_TIER_SETTINGS.goldMinSpending));
  const platinumMinSpending = Math.max(goldMinSpending, Number(settings.platinumMinSpending ?? DEFAULT_MEMBERSHIP_TIER_SETTINGS.platinumMinSpending));

  const silverMinRef = Math.max(0, Number(settings.silverMinReferrals ?? DEFAULT_MEMBERSHIP_TIER_SETTINGS.silverMinReferrals));
  const goldMinRef = Math.max(silverMinRef + 1, Number(settings.goldMinReferrals ?? DEFAULT_MEMBERSHIP_TIER_SETTINGS.goldMinReferrals));
  const platinumMinRef = Math.max(goldMinRef + 1, Number(settings.platinumMinReferrals ?? DEFAULT_MEMBERSHIP_TIER_SETTINGS.platinumMinReferrals));

  // STRICT AND RULE EVALUATION
  const isPlatinumTx = count >= platinumMinTx;
  const isPlatinumSpending = spending >= platinumMinSpending;
  const isPlatinum = isPlatinumTx && isPlatinumSpending;

  const isGoldTx = count >= goldMinTx;
  const isGoldSpending = spending >= goldMinSpending;
  const isGold = (isGoldTx && isGoldSpending) || isPlatinum;

  if (isPlatinum) {
    return {
      tier: 'PLATINUM',
      tierName: 'Platinum',
      tierBadge: '💎 PLATINUM',
      transactionCount: count,
      validSpending: spending,
      successfulReferrals: refCount,
      nextTier: null,
      nextTierName: null,
      remainingTransactions: 0,
      remainingSpending: 0,
      remainingReferrals: 0,
      isTransactionsMet: true,
      isSpendingMet: true,
      progressPercent: 100,
      statusMessage: 'LEVEL TERTINGGI',
      theme: {
        badgeBg: 'bg-gradient-to-r from-cyan-500/20 via-blue-500/20 to-purple-500/20',
        badgeText: 'text-cyan-300 font-extrabold',
        cardGradient: 'from-[#0B1528] via-[#0F172A] to-[#1E1B4B]',
        accentColor: '#38BDF8',
        borderAccent: 'border-cyan-500/30',
        iconEmoji: '💎',
      },
    };
  }

  if (isGold) {
    const remainingTx = Math.max(0, platinumMinTx - count);
    const remainingSpending = Math.max(0, platinumMinSpending - spending);
    const remainingRef = Math.max(0, platinumMinRef - refCount);

    const progressTx = Math.min(100, Math.max(0, Math.round(((count - goldMinTx) / Math.max(1, platinumMinTx - goldMinTx)) * 100)));
    const progressSpend = platinumMinSpending > goldMinSpending
      ? Math.min(100, Math.max(0, Math.round(((spending - goldMinSpending) / Math.max(1, platinumMinSpending - goldMinSpending)) * 100)))
      : 100;

    const progressOverall = Math.min(progressTx, progressSpend);

    let msg = '';
    if (remainingTx > 0 && remainingSpending > 0) {
      msg = `${remainingTx} transaksi & ${formatRupiah(remainingSpending)} lagi menuju Platinum`;
    } else if (remainingTx > 0) {
      msg = `${remainingTx} transaksi lagi menuju Platinum`;
    } else if (remainingSpending > 0) {
      msg = `${formatRupiah(remainingSpending)} belanja lagi menuju Platinum`;
    } else {
      msg = 'Memenuhi syarat Platinum';
    }

    return {
      tier: 'GOLD',
      tierName: 'Gold',
      tierBadge: '🥇 GOLD',
      transactionCount: count,
      validSpending: spending,
      successfulReferrals: refCount,
      nextTier: 'PLATINUM',
      nextTierName: 'Platinum',
      remainingTransactions: remainingTx,
      remainingSpending: remainingSpending,
      remainingReferrals: remainingRef,
      isTransactionsMet: count >= platinumMinTx,
      isSpendingMet: spending >= platinumMinSpending,
      progressPercent: progressOverall,
      statusMessage: msg,
      theme: {
        badgeBg: 'bg-gradient-to-r from-amber-500/20 to-yellow-500/20',
        badgeText: 'text-amber-400 font-extrabold',
        cardGradient: 'from-[#1E293B] via-[#0F172A] to-[#1E293B]',
        accentColor: '#F59E0B',
        borderAccent: 'border-amber-500/30',
        iconEmoji: '🥇',
      },
    };
  }

  // Silver (or locked Silver if silverMinTx > 0 or silverMinSpending > 0)
  if (count < silverMinTx || spending < silverMinSpending) {
    const remainingSilverTx = Math.max(0, silverMinTx - count);
    const remainingSilverSpend = Math.max(0, silverMinSpending - spending);
    const progressSilverTx = silverMinTx > 0 ? Math.min(100, Math.max(0, Math.round((count / Math.max(1, silverMinTx)) * 100))) : 100;
    const progressSilverSpend = silverMinSpending > 0 ? Math.min(100, Math.max(0, Math.round((spending / Math.max(1, silverMinSpending)) * 100))) : 100;
    const progressSilver = Math.min(progressSilverTx, progressSilverSpend);

    let lockMsg = '';
    if (remainingSilverTx > 0 && remainingSilverSpend > 0) {
      lockMsg = `${remainingSilverTx} transaksi & ${formatRupiah(remainingSilverSpend)} lagi untuk membuka Silver Tier`;
    } else if (remainingSilverTx > 0) {
      lockMsg = `${remainingSilverTx} transaksi lagi untuk membuka Silver Tier`;
    } else {
      lockMsg = `${formatRupiah(remainingSilverSpend)} belanja lagi untuk membuka Silver Tier`;
    }

    return {
      tier: 'SILVER',
      tierName: 'Silver',
      tierBadge: '🥈 SILVER',
      transactionCount: count,
      validSpending: spending,
      successfulReferrals: refCount,
      nextTier: 'SILVER',
      nextTierName: 'Silver',
      remainingTransactions: remainingSilverTx,
      remainingSpending: remainingSilverSpend,
      remainingReferrals: Math.max(0, silverMinRef - refCount),
      isTransactionsMet: count >= silverMinTx,
      isSpendingMet: spending >= silverMinSpending,
      progressPercent: progressSilver,
      statusMessage: lockMsg,
      theme: {
        badgeBg: 'bg-gradient-to-r from-slate-400/20 to-slate-200/20',
        badgeText: 'text-slate-300 font-extrabold',
        cardGradient: 'from-[#1E222A] via-[#141820] to-[#1E222A]',
        accentColor: '#94A3B8',
        borderAccent: 'border-slate-500/30',
        iconEmoji: '🥈',
      },
    };
  }

  const remainingTx = Math.max(0, goldMinTx - count);
  const remainingSpending = Math.max(0, goldMinSpending - spending);
  const remainingRef = Math.max(0, goldMinRef - refCount);

  const progressTx = Math.min(100, Math.max(0, Math.round(((count - silverMinTx) / Math.max(1, goldMinTx - silverMinTx)) * 100)));
  const progressSpend = goldMinSpending > silverMinSpending
    ? Math.min(100, Math.max(0, Math.round(((spending - silverMinSpending) / Math.max(1, goldMinSpending - silverMinSpending)) * 100)))
    : 100;

  const progressOverall = Math.min(progressTx, progressSpend);

  let msg = '';
  if (remainingTx > 0 && remainingSpending > 0) {
    msg = `${remainingTx} transaksi & ${formatRupiah(remainingSpending)} lagi menuju Gold`;
  } else if (remainingTx > 0) {
    msg = `${remainingTx} transaksi lagi menuju Gold`;
  } else if (remainingSpending > 0) {
    msg = `${formatRupiah(remainingSpending)} belanja lagi menuju Gold`;
  } else {
    msg = 'Memenuhi syarat Gold';
  }

  return {
    tier: 'SILVER',
    tierName: 'Silver',
    tierBadge: '🥈 SILVER',
    transactionCount: count,
    validSpending: spending,
    successfulReferrals: refCount,
    nextTier: 'GOLD',
    nextTierName: 'Gold',
    remainingTransactions: remainingTx,
    remainingSpending: remainingSpending,
    remainingReferrals: remainingRef,
    isTransactionsMet: count >= goldMinTx,
    isSpendingMet: spending >= goldMinSpending,
    progressPercent: progressOverall,
    statusMessage: msg,
    theme: {
      badgeBg: 'bg-gradient-to-r from-slate-400/20 to-slate-200/20',
      badgeText: 'text-slate-300 font-extrabold',
      cardGradient: 'from-[#1E222A] via-[#141820] to-[#1E222A]',
      accentColor: '#94A3B8',
      borderAccent: 'border-slate-500/30',
      iconEmoji: '🥈',
    },
  };
}

/**
 * Fetch and count customer's valid orders directly from public.orders with verified order_items in Supabase.
 * Strictly verifies:
 * 1. public.orders record exists
 * 2. order_items table has matching records (prevents counting orders where order_items RLS insert failed)
 * 3. Payment is successful (PAID or completed pay-at-store)
 * 4. Order is NOT cancelled, rejected, or failed.
 */
export async function fetchCustomerValidOrdersCount(
  customerId?: string,
  customerPhone?: string
): Promise<{ count: number; totalSpending: number; validOrders: CustomerOrder[]; allOrders: CustomerOrder[] }> {
  if (!customerId && !customerPhone) {
    return { count: 0, totalSpending: 0, validOrders: [], allOrders: [] };
  }

  try {
    const client = getSupabase();
    let query = client
      .from('orders')
      .select('*')
      .order('created_at', { ascending: false });

    if (customerId) {
      query = query.eq('customer_id', customerId);
    }

    const { data: ordersData, error } = await query;
    let rawList: any[] = [];

    if (!error && Array.isArray(ordersData)) {
      rawList = ordersData;
    }

    // Phone fallback if no customer_id rows found
    if (customerPhone && rawList.length === 0) {
      const cleanPhone = customerPhone.replace(/[^0-9]/g, '');
      if (cleanPhone.length >= 8) {
        const { data: phoneData } = await client
          .from('orders')
          .select('*')
          .ilike('customer_phone', `%${cleanPhone.slice(-8)}%`)
          .order('created_at', { ascending: false });

        if (Array.isArray(phoneData)) {
          rawList = phoneData;
        }
      }
    }

    if (rawList.length === 0) {
      return { count: 0, totalSpending: 0, validOrders: [], allOrders: [] };
    }

    // Batch query order_items to verify order_items exist in database
    const orderIds = rawList.map((r: any) => r.id).filter(Boolean);
    const orderItemsMap: Record<string, any[]> = {};

    if (orderIds.length > 0) {
      try {
        const { data: itemsRows, error: itemsErr } = await client
          .from('order_items')
          .select('id, order_id, product_id, name, price, quantity')
          .in('order_id', orderIds);

        if (!itemsErr && Array.isArray(itemsRows)) {
          itemsRows.forEach((item: any) => {
            if (item && item.order_id) {
              if (!orderItemsMap[item.order_id]) {
                orderItemsMap[item.order_id] = [];
              }
              orderItemsMap[item.order_id].push(item);
            }
          });
        }
      } catch (itemQueryErr) {
        console.warn('[MembershipTier] Error fetching order_items batch:', itemQueryErr);
      }
    }

    // Map rows to CustomerOrder format with attached order_items verification
    const mapped: (CustomerOrder & { order_items?: any[] })[] = rawList.map((row: any) => {
      const attachedItems = orderItemsMap[row.id];
      const jsonItems = Array.isArray(row.items) ? row.items : [];

      return {
        id: row.id,
        orderNumber: row.order_number || row.orderNumber || 'LTN-????',
        outletId: row.outlet_id || row.outletId || '',
        outletName: row.outlet_name || row.outletName || '',
        customerName: row.customer_name || row.customerName || '',
        customerPhone: row.customer_phone || row.customerPhone || '',
        customerId: row.customer_id || row.customerId || undefined,
        userId: row.user_id || row.userId || undefined,
        orderType: row.order_type || row.orderType || 'DINE IN',
        tableNumber: row.table_number || row.tableNumber || '',
        items: jsonItems,
        // Verified items from public.order_items table
        order_items: attachedItems !== undefined ? attachedItems : jsonItems,
        totalAmount: Number(row.total_amount || row.totalAmount || 0),
        paymentMethod: row.payment_method || row.paymentMethod || 'QRIS',
        paymentStatus: row.payment_status || row.paymentStatus || 'WAITING PAYMENT',
        paymentReceiptUrl: row.payment_receipt_url || row.paymentReceiptUrl,
        paymentReceiptPath: row.payment_receipt_path || row.paymentReceiptPath,
        paymentProofPath: row.payment_proof_path || row.paymentProofPath,
        rejectionReason: row.rejection_reason || row.rejectionReason,
        orderStatus: row.order_status || row.orderStatus || 'NEW',
        customerNote: row.customer_note || row.customerNote || '',
        createdAt: row.created_at || row.createdAt,
        updatedAt: row.updated_at || row.updatedAt,
      };
    });

    // Filter valid orders according to strict membership rules
    const valid = mapped.filter((o) => isOrderValidForTier(o));
    const totalSpending = valid.reduce((sum, o) => sum + (Number(o.totalAmount) || 0), 0);

    return {
      count: valid.length,
      totalSpending,
      validOrders: valid,
      allOrders: mapped,
    };
  } catch (err) {
    console.warn('[MembershipTier] Error counting customer valid orders:', err);
    return { count: 0, totalSpending: 0, validOrders: [], allOrders: [] };
  }
}

/**
 * Fetch customer's successful referrals count from authoritative database tables.
 * Strict rules:
 * 1. Counts referred customers in public.customers where referred_by = customerId AND referral_rewarded = true.
 * 2. Cross-verifies with public.loyalty_transactions where customer_id = customerId AND transaction_type = 'EARN' AND reason indicates Referral Reward.
 * 3. Never counts unrewarded / pending referrals.
 */
export async function fetchCustomerSuccessfulReferralsCount(customerId: string): Promise<number> {
  if (!customerId) return 0;
  try {
    const client = getSupabase();

    // 1. Check customers table where referred_by = customerId and reward has been issued (referral_rewarded = true)
    const { data: referredCustomers, error: custErr } = await client
      .from('customers')
      .select('id, referral_rewarded')
      .eq('referred_by', customerId)
      .eq('referral_rewarded', true);

    const fromCustTable = !custErr && Array.isArray(referredCustomers) ? referredCustomers.length : 0;

    // 2. Also check loyalty_transactions ledger for referrer EARN rewards
    const { data: txRows, error: txErr } = await client
      .from('loyalty_transactions')
      .select('id, reason')
      .eq('customer_id', customerId)
      .eq('transaction_type', 'EARN')
      .ilike('reason', '%Referral Reward%');

    // Filter to ensure only referrer rewards (not "Referred Member")
    const fromTxTable = !txErr && Array.isArray(txRows)
      ? txRows.filter((t: any) => !t.reason?.toLowerCase().includes('referred member')).length
      : 0;

    return Math.max(fromCustTable, fromTxTable);
  } catch (err) {
    console.warn('[MembershipTier] Error counting successful referrals:', err);
    return 0;
  }
}

